import { forwardRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import Link from '@base/Link/Link';
import dateHelper from '@utils/dates';
import textHelper from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { mediaQuery, Spacings } from '@styles/index';
import useWindowSize from '@hooks/useWindowSize';
import AppInstallBanner from '@comp/AppInstallBanner/Version1';
import { LESSON_PAGE } from '@constants/appFlyerLinks';
import FreeLessonCard from './FreeLessonCard';

const StyledFreeLessonCard = styled(FreeLessonCard)`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
`;

const ListHeaderWrapper = styled.div`
  grid-column: span 8;
  padding: ${Spacings.SPACING_4B};
  border-bottom: 1px solid var(--color-shadow);
  ${mediaQuery.uptoMobile} {
    border-bottom: 0;
    grid-column: span 16;
    padding: ${Spacings.SPACING_3B} 16px ${Spacings.SPACING_2B};
  }
`;

const ListWrapper = styled.div`
  grid-column: span 8;
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  height: 746px;
  display: flex;
  flex-direction: column;
  top: 80px;
  ${mediaQuery.uptoTablet} {
    grid-column: span 16;
    top: 0;
    position: relative;
    height: auto;
  }
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    margin: 0 -${Spacings.SPACING_4B};
    background-color: inherit;
    box-shadow: none;
    height: auto;
  }
`;

const ListWrapperInner = styled.div`
  grid-column: span 8;
  flex: 1;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow-y: auto;
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
  }
`;

const CourseTitle = styled(H6)`
  cursor: pointer;
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_1B};
  }
`;

const FreeLessonList = ({ freeLessonList, freeLessonInfo }, ref) => {
  const [openBanner, setOpenBanner] = useState(false);
  const { isMobile } = useWindowSize();
  const getTotalTimeOfCourse = (lessonList) => {
    let totalTime = 0;
    lessonList.forEach((lesson) => {
      const lessonDuration =
        lesson?.value?.videoDuration ||
        lesson?.value?.video?.duration ||
        lesson?.value?.duration ||
        0;
      totalTime += lessonDuration;
    });

    totalTime = dateHelper.getTimeFormatInShortFormWithoutSecs(totalTime);
    return totalTime;
  };

  const doNothing = (e) => {
    if (isMobile) {
      e.preventDefault();
    }
  };

  const showBanner = () => setOpenBanner(true);
  const hideBanner = () => setOpenBanner(false);
  const renderListHeader = () => {
    const {
      name: courseTitle,
      itemCount,
      slug,
      uid
    } = freeLessonInfo?.collection;
    const totalTime = getTotalTimeOfCourse(freeLessonList);

    return (
      <ListHeaderWrapper>
        <Link
          as={`/course/${slug}/${uid}`}
          href="/course/[slug]/[courseUID]"
          onClick={doNothing}
        >
          <CourseTitle as="h2">{courseTitle}</CourseTitle>
        </Link>
        <P2 color="var(--color-text-secondary)">
          {`${textHelper.pluralize(itemCount, 'lesson')} • ${totalTime}`}
        </P2>
      </ListHeaderWrapper>
    );
  };

  const renderList = () =>
    freeLessonList.map((lesson) => (
      <StyledFreeLessonCard
        key={lesson?.value?.uid}
        lesson={lesson?.value}
        lessonRank={lesson?.rank}
        watchingUID={freeLessonInfo.uid}
        onClick={showBanner}
        isMobile={isMobile}
      />
    ));

  return (
    <>
      <ListWrapper ref={ref}>
        {renderListHeader()}
        <ListWrapperInner>{renderList()}</ListWrapperInner>
      </ListWrapper>
      <AppInstallBanner
        open={openBanner}
        onClose={hideBanner}
        installLink={LESSON_PAGE}
      />
    </>
  );
};

export default forwardRef(FreeLessonList);

FreeLessonList.propTypes = {
  freeLessonList: PropTypes.arrayOf(PropTypes.any).isRequired,
  freeLessonInfo: PropTypes.objectOf(PropTypes.any).isRequired
};
